import type {
    MutableToolStep,
    MutableToolStepKey,
} from '@toolproof-core/lib/_contract/workflow-overlay';


export type MutableCasePair = {
    ownerMacroStepKey: string;
    ownerCaseIndex: number;
    slotFamily: 'case' | 'cases';
    whenToolStepKey?: MutableToolStepKey;
    whatToolStepKey?: MutableToolStepKey;
};

export function buildStepIndexByStepKey(args: {
    orderedToolSteps: MutableToolStep[];
}): Map<MutableToolStepKey, number> {
    const out = new Map<MutableToolStepKey, number>();
    args.orderedToolSteps.forEach((toolStep, index) => {
        out.set(toolStep.stepKey as MutableToolStepKey, index);
    });
    return out;
}

export function parseAuthoredCaseWhenAddressKey(addressKey: string): {
    ownerMacroStepKey: string;
    ownerCaseIndex: number;
} | null {
    const [ownerMacroStepKey, ownerCaseIndexRaw, slotInOwner] = addressKey.split('|');
    if (!ownerMacroStepKey || !ownerCaseIndexRaw || !slotInOwner) return null;
    if (slotInOwner !== 'cases/when') return null;

    const ownerCaseIndex = Number.parseInt(ownerCaseIndexRaw, 10);
    if (!Number.isFinite(ownerCaseIndex) || ownerCaseIndex < 0) return null;

    return {
        ownerMacroStepKey,
        ownerCaseIndex,
    };
}

export function isLoopCaseSlot(slotInOwner: string): boolean {
    return slotInOwner === 'case/when' || slotInOwner === 'case/what';
}

export function upsertCasePair(args: {
    pairByKey: Map<string, MutableCasePair>;
    pairKey: string;
    ownerMacroStepKey: string;
    ownerCaseIndex: number;
    slotFamily: 'case' | 'cases';
    slotInOwner: string;
    stepKey: MutableToolStepKey;
}): void {
    const {
        pairByKey,
        pairKey,
        ownerMacroStepKey,
        ownerCaseIndex,
        slotFamily,
        slotInOwner,
        stepKey,
    } = args;

    const pair = pairByKey.get(pairKey) ?? {
        ownerMacroStepKey,
        ownerCaseIndex,
        slotFamily,
    };

    if (slotInOwner.endsWith('/when')) {
        pair.whenToolStepKey = stepKey;
    } else {
        pair.whatToolStepKey = stepKey;
    }

    pairByKey.set(pairKey, pair);
}

export function resolveCasePairStepIndexes(args: {
    pair: MutableCasePair;
    stepIndexByStepKey: ReadonlyMap<MutableToolStepKey, number>;
}): {
    whenStepIndex: number;
    whatStepIndex: number;
} | null {
    const { pair, stepIndexByStepKey } = args;
    if (!pair.whenToolStepKey || !pair.whatToolStepKey) return null;

    const whenStepIndex = stepIndexByStepKey.get(pair.whenToolStepKey);
    const whatStepIndex = stepIndexByStepKey.get(pair.whatToolStepKey);
    if (whenStepIndex == null || whatStepIndex == null) return null;

    return {
        whenStepIndex,
        whatStepIndex,
    };
}
